import { normalizeProjectPath, PathNormalizationError } from '../path';
import type { GitignoreSourceInput, InputIssueCode, PathInputIssue } from '../../types';

export const MAX_PROJECT_PATHS = 25_000;
export const MAX_GITIGNORE_BYTES = 1024 * 1024;
export const DIRECTORY_YIELD_INTERVAL = 500;

export type FileInputIssueCode =
  | InputIssueCode
  | 'not-gitignore'
  | 'binary-gitignore'
  | 'invalid-encoding'
  | 'gitignore-too-large'
  | 'read-failed'
  | 'path-limit-exceeded';

export type FileInputIssueSeverity = 'error' | 'warning';

export interface FileInputIssue {
  readonly code: FileInputIssueCode;
  readonly input: string;
  readonly message: string;
  readonly severity: FileInputIssueSeverity;
}

export interface LoadGitignoreFileResult {
  readonly source: GitignoreSourceInput;
  readonly issues: readonly FileInputIssue[];
}

export class FileInputError extends Error {
  readonly issues: readonly FileInputIssue[];

  constructor(message: string, issues: readonly FileInputIssue[]) {
    super(message);
    this.name = 'FileInputError';
    this.issues = issues;
  }
}

export interface DirectoryLoadProgress {
  readonly processed: number;
  readonly total: number;
  readonly gitignoreFilesRead: number;
}

export interface LoadProjectDirectoryOptions {
  readonly maxPaths?: number;
  readonly maxGitignoreBytes?: number;
  readonly onProgress?: (progress: DirectoryLoadProgress) => void;
}

export interface LoadProjectDirectoryResult {
  readonly sources: readonly GitignoreSourceInput[];
  readonly paths: readonly string[];
  readonly projectName: string;
  readonly issues: readonly FileInputIssue[];
  readonly truncated: boolean;
}

export interface ParsePastedPathsResult {
  readonly paths: readonly string[];
  readonly issues: readonly PathInputIssue[];
}

export async function loadGitignoreFile(file: File): Promise<LoadGitignoreFileResult> {
  if (file.name !== '.gitignore') {
    const issues: readonly FileInputIssue[] = [
      {
        code: 'not-gitignore',
        input: file.name,
        message: 'Choose a file named `.gitignore`. The selected file was not read.',
        severity: 'error',
      },
    ];
    throw new FileInputError(
      issues[0]?.message ?? 'The selected file is not a .gitignore.',
      issues,
    );
  }

  const result = await readGitignore(file, '.gitignore', MAX_GITIGNORE_BYTES);
  if (result.source === null) {
    throw new FileInputError(
      result.issues[0]?.message ?? 'The selected .gitignore could not be read.',
      result.issues,
    );
  }
  return { source: result.source, issues: result.issues };
}

export async function loadProjectDirectory(
  files: FileList | readonly File[],
  options: LoadProjectDirectoryOptions = {},
): Promise<LoadProjectDirectoryResult> {
  const allFiles = Array.from(files);
  const maxPaths = Math.max(1, options.maxPaths ?? MAX_PROJECT_PATHS);
  const maxGitignoreBytes = Math.max(1, options.maxGitignoreBytes ?? MAX_GITIGNORE_BYTES);
  let truncated = allFiles.length > maxPaths;
  const selectedFiles = allFiles.slice(0, maxPaths);
  const rawPaths = selectedFiles.map(rawFilePath);
  const sharedRoot = findSharedRoot(rawPaths);
  const projectName = sharedRoot ?? '';
  const sources: GitignoreSourceInput[] = [];
  const paths: string[] = [];
  const issues: FileInputIssue[] = [];
  const seenPaths = new Set<string>();
  const seenNodes = new Set<string>();
  let gitignoreFilesRead = 0;
  let pathLimitIssueAdded = false;

  const publishProgress = (processed: number): boolean => {
    options.onProgress?.({
      processed,
      total: selectedFiles.length,
      gitignoreFilesRead,
    });
    return processed < selectedFiles.length && processed % DIRECTORY_YIELD_INTERVAL === 0;
  };

  if (truncated) {
    issues.push({
      code: 'path-limit-exceeded',
      input: String(allFiles.length),
      message: `The folder exceeds the ${String(maxPaths)}-path analysis limit. Remaining entries were not read.`,
      severity: 'warning',
    });
    pathLimitIssueAdded = true;
  }

  for (const [index, file] of selectedFiles.entries()) {
    const rawPath = rawPaths[index];
    if (rawPath === undefined) {
      continue;
    }

    const candidate = stripSharedRoot(rawPath, sharedRoot);
    let path: string;
    try {
      path = normalizeProjectPath(candidate, { kind: 'file' });
    } catch (error: unknown) {
      if (error instanceof PathNormalizationError) {
        issues.push({
          code: error.code,
          input: error.input,
          message: error.message,
          severity: 'error',
        });
        if (publishProgress(index + 1)) {
          await yieldToBrowser();
        }
        continue;
      }
      throw error;
    }

    if (seenPaths.has(path)) {
      issues.push({
        code: 'duplicate-path',
        input: candidate,
        message: `Duplicate path \`${path}\` was included once.`,
        severity: 'warning',
      });
      if (publishProgress(index + 1)) {
        await yieldToBrowser();
      }
      continue;
    }

    const addedNodes = inferredNodePaths(path).filter((node) => !seenNodes.has(node));
    if (seenNodes.size + addedNodes.length > maxPaths) {
      truncated = true;
      if (!pathLimitIssueAdded) {
        issues.push({
          code: 'path-limit-exceeded',
          input: String(allFiles.length),
          message: `Analysis stopped before the inferred path tree would exceed ${String(maxPaths)} nodes. Remaining entries were not read.`,
          severity: 'warning',
        });
      }
      if (publishProgress(index + 1)) {
        await yieldToBrowser();
      }
      break;
    }

    for (const node of addedNodes) {
      seenNodes.add(node);
    }
    seenPaths.add(path);
    paths.push(path);

    if (isGitignorePath(path)) {
      const result = await readGitignore(file, path, maxGitignoreBytes);
      issues.push(...result.issues);
      if (result.source !== null) {
        sources.push(result.source);
        gitignoreFilesRead += 1;
      }
    }

    if (publishProgress(index + 1)) {
      await yieldToBrowser();
    }
  }

  return {
    sources: sources.sort((left, right) => left.path.localeCompare(right.path, 'en')),
    paths: paths.sort((left, right) => left.localeCompare(right, 'en')),
    projectName,
    issues,
    truncated,
  };
}

export function parsePastedPaths(text: string): ParsePastedPathsResult {
  const paths: string[] = [];
  const issues: PathInputIssue[] = [];
  const seen = new Set<string>();

  for (const rawLine of text.split(/\r?\n/u)) {
    const input = rawLine.trim();
    if (input.length === 0) {
      continue;
    }

    try {
      const path = normalizeProjectPath(input);
      if (seen.has(path)) {
        issues.push({
          code: 'duplicate-path',
          input,
          message: `Duplicate path \`${path}\` was included once.`,
        });
        continue;
      }
      seen.add(path);
      paths.push(path);
    } catch (error: unknown) {
      if (error instanceof PathNormalizationError) {
        issues.push({ code: error.code, input: error.input, message: error.message });
        continue;
      }
      throw error;
    }
  }

  return { paths, issues };
}

interface ReadGitignoreResult {
  readonly source: GitignoreSourceInput | null;
  readonly issues: readonly FileInputIssue[];
}

async function readGitignore(
  file: File,
  path: string,
  maxBytes: number,
): Promise<ReadGitignoreResult> {
  if (file.size > maxBytes) {
    return {
      source: null,
      issues: [
        {
          code: 'gitignore-too-large',
          input: path,
          message: `\`${path}\` is larger than ${String(maxBytes)} bytes and was not read.`,
          severity: 'error',
        },
      ],
    };
  }

  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await file.arrayBuffer());
  } catch {
    return {
      source: null,
      issues: [
        {
          code: 'read-failed',
          input: path,
          message: `The browser could not read \`${path}\`. Other selected input was preserved.`,
          severity: 'error',
        },
      ],
    };
  }

  if (bytes.includes(0)) {
    return {
      source: null,
      issues: [
        {
          code: 'binary-gitignore',
          input: path,
          message: `\`${path}\` appears to be binary and was not analyzed.`,
          severity: 'error',
        },
      ],
    };
  }

  let content: string;
  try {
    content = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return {
      source: null,
      issues: [
        {
          code: 'invalid-encoding',
          input: path,
          message: `\`${path}\` is not valid UTF-8 and was not analyzed.`,
          severity: 'error',
        },
      ],
    };
  }

  return {
    source: { path, content: content.replace(/^\uFEFF/u, '') },
    issues: [],
  };
}

function rawFilePath(file: File): string {
  return file.webkitRelativePath.length > 0 ? file.webkitRelativePath : file.name;
}

function findSharedRoot(paths: readonly string[]): string | null {
  if (paths.length === 0) {
    return null;
  }

  const normalized = paths.map((path) => path.replaceAll('\\', '/'));
  if (normalized.some(isAbsoluteLike)) {
    return null;
  }

  const first = normalized[0];
  if (first === undefined) {
    return null;
  }
  const separator = first.indexOf('/');
  if (separator <= 0) {
    return null;
  }
  const root = first.slice(0, separator);
  if (!isSafeRootSegment(root)) {
    return null;
  }

  return normalized.every((path) => path.startsWith(`${root}/`)) ? root : null;
}

function stripSharedRoot(path: string, root: string | null): string {
  const normalized = path.replaceAll('\\', '/');
  return root === null ? normalized : normalized.slice(root.length + 1);
}

function isGitignorePath(path: string): boolean {
  return path === '.gitignore' || path.endsWith('/.gitignore');
}

function inferredNodePaths(path: string): string[] {
  const bare = path.endsWith('/') ? path.slice(0, -1) : path;
  const segments = bare.split('/');
  const nodes: string[] = [];
  for (let index = 1; index < segments.length; index += 1) {
    nodes.push(`${segments.slice(0, index).join('/')}/`);
  }
  nodes.push(path);
  return nodes;
}

function isAbsoluteLike(path: string): boolean {
  return /^(?:[A-Za-z]:|\/|\\)/u.test(path);
}

function isSafeRootSegment(segment: string): boolean {
  return (
    segment !== '.' &&
    segment !== '..' &&
    !/^[A-Za-z]:$/u.test(segment) &&
    !hasControlCharacters(segment)
  );
}

function hasControlCharacters(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 32 || code === 127) {
      return true;
    }
  }
  return false;
}

function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}
